import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, FileDown, BookOpen } from 'lucide-react';
import { FaFacebook, FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa';
import { contactData } from '../data/mock';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { toast } from 'sonner';

const iconMap = {
  Facebook: FaFacebook,
  Instagram: FaInstagram,
  TikTok: FaTiktok,
  Youtube: FaYoutube
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    if (formData.name && formData.email && formData.message) {
      toast.success('¡Mensaje enviado! Nos pondremos en contacto pronto.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } else {
      toast.error('Por favor completa todos los campos requeridos.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contacto" className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {contactData.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {contactData.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <Card className="bg-gray-900 border-gray-800 hover:border-amber-500/50 transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                      <Phone className="text-black" size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Teléfono</h3>
                    <p className="text-gray-400">{contactData.info.phone}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800 hover:border-amber-500/50 transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                      <Mail className="text-black" size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Email</h3>
                    <p className="text-gray-400">{contactData.info.email}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800 hover:border-amber-500/50 transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                      <MapPin className="text-black" size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Dirección</h3>
                    <p className="text-gray-400 whitespace-pre-line">{contactData.info.address}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800 hover:border-amber-500/50 transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                      <Clock className="text-black" size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Horario</h3>
                    <p className="text-gray-400 whitespace-pre-line">{contactData.info.hours}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Social Media */}
            <div>
              <h3 className="text-white font-semibold mb-4">Síguenos</h3>
              <div className="flex flex-wrap gap-3">
                {contactData.socialMedia.map((social) => {
                  const IconComponent = iconMap[social.icon];
                  return (
                    <a
                      key={social.platform}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-gray-800 hover:bg-gradient-to-br hover:from-amber-500 hover:to-amber-600 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 group"
                      title={social.platform}
                    >
                      <IconComponent className="text-gray-400 group-hover:text-black transition-colors" size={24} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-300 mb-2 font-medium">
                    Nombre *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Tu nombre completo"
                    className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500 transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">
                    Email *
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500 transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">
                    Teléfono
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+34 123 456 789"
                    className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">
                    Mensaje *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Cuéntanos sobre tu interés en nuestras clases..."
                    rows={5}
                    className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500 transition-colors resize-none"
                    required
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold text-lg py-6 shadow-lg shadow-amber-500/50 transition-all duration-300 hover:scale-105"
                >
                  <Send className="mr-2" size={20} />
                  Enviar Mensaje
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Cuaderno MASTER-CLASS PDF */}
        <Card data-testid="cuaderno-card" className="mt-12 bg-gray-900 border-gray-800 hover:border-amber-500/50 transition-all duration-300">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center">
                  <BookOpen className="text-black" size={40} />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold text-white mb-3">Cuaderno MASTER-CLASS</h3>
                <p className="text-gray-400 leading-relaxed">
                  En MASTER-CLASS creemos que aprender música es mucho más que aprender canciones. Por eso hemos preparado este pequeño Cuaderno MASTER-CLASS. En él te contamos quiénes somos, cómo enseñamos y, sobre todo, lo importante que es para nosotros conocer antes a la persona que enseñar música. Si estás pensando en aprender música —o buscas un lugar para alguien importante para ti— te invitamos a dedicarle un minuto.
                </p>
              </div>
              <div className="flex-shrink-0">
                <a
                  href="/assets/cuaderno_masterclass.pdf"
                  download="Cuaderno MASTER-CLASS.pdf"
                  data-testid="download-cuaderno-btn"
                  className="inline-flex items-center bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold text-lg px-8 py-4 rounded-lg shadow-lg shadow-amber-500/50 transition-all duration-300 hover:scale-105"
                >
                  <FileDown className="mr-2" size={22} />
                  Descargar PDF
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default Contact;
